/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { produce } from 'immer';
import type { Element } from '@src/web-stories-wp/story-editor/src/elements/types';

/**
 * Internal dependencies
 */
import type { ReducerState, ReducerStateDraft } from '../../../../types';
import { ensureGroupLayersAreAdjacent, getAbsolutePosition } from './utils';

enum LayerDirection {
  Front = 'FRONT',
  Back = 'BACK',
  Forward = 'FORWARD',
  Backward = 'BACKWARD',
}

type ArrangeGroupProps = {
  groupId: string;
  position: number | LayerDirection;
};

/**
 * Move group to a new position
 */
export const arrangeGroup = (
  draft: ReducerStateDraft,
  { groupId, position }: ArrangeGroupProps
) => {
  if (!groupId) {
    return;
  }

  const page = draft.pages.find(({ id }) => id === draft.current);
  if (!page) {
    return;
  }
  let { elements, groups } = page;
  const isInGroup = (el: Element) => el.groupId === groupId;

  // If group doesn't exist or doesn't have any elements, abort
  if (!groups?.[groupId] || !elements.some(isInGroup)) {
    return;
  }

  // Find group position in elements array
  const groupStartIndex = elements.findIndex(isInGroup);
  const groupEndIndex = getLastIndexByPredicate(elements, isInGroup);
  const groupSize = groupEndIndex - groupStartIndex + 1;
  let resolvedPosition = position;

  if (
    position === LayerDirection.Forward ||
    position === LayerDirection.Backward
  ) {
    resolvedPosition = resolveDirectionalPosition(
      elements,
      groupStartIndex,
      groupEndIndex,
      position
    );
    if (resolvedPosition === null) {
      return;
    }
  }

  const groupWasMovedUpwards =
    typeof resolvedPosition === 'number' && resolvedPosition > groupEndIndex;

  const newPositionOffset = groupWasMovedUpwards ? (groupSize - 1) * -1 : 0;

  // Splice out the entire group and replace with dummy entry
  const groupSlice = elements.splice(
    groupStartIndex,
    groupSize,
    'dummy' as unknown as Element
  );

  // Dummy entry is needed to calculate the new position as if the group is one entry
  const minPosition = 1;
  const maxPosition = elements.length - 1;
  const newPosition = getAbsolutePosition({
    currentPosition: groupStartIndex,
    minPosition,
    maxPosition,
    desiredPosition:
      typeof resolvedPosition === 'number'
        ? resolvedPosition + newPositionOffset
        : resolvedPosition,
  });

  // remove the dummy entry
  elements.splice(elements.indexOf('dummy' as unknown as Element), 1);

  // Insert the new elements at position
  elements.splice(newPosition, 0, ...groupSlice);

  page.elements = ensureGroupLayersAreAdjacent(elements);
};

function resolveDirectionalPosition(
  elements: Element[],
  groupStartIndex: number,
  groupEndIndex: number,
  position: LayerDirection
) {
  if (position === LayerDirection.Forward) {
    const nextIndex = groupEndIndex + 1;
    if (nextIndex >= elements.length) {
      return null;
    }

    const nextElement = elements[nextIndex];
    if (!nextElement?.groupId) {
      return nextIndex;
    }

    return getLastIndexByPredicate(
      elements,
      (element) => element.groupId === nextElement.groupId
    );
  }

  if (position === LayerDirection.Backward) {
    const prevIndex = groupStartIndex - 1;
    if (prevIndex <= 0) {
      return null;
    }

    const prevElement = elements[prevIndex];
    if (!prevElement?.groupId) {
      return prevIndex;
    }

    return elements.findIndex(
      (element) => element.groupId === prevElement.groupId
    );
  }

  return position;
}

function getLastIndexByPredicate(
  elements: Element[],
  predicate: (element: Element) => boolean
) {
  for (let index = elements.length - 1; index >= 0; index--) {
    if (predicate(elements[index])) {
      return index;
    }
  }

  return -1;
}

// @ts-ignore
export default produce<ReducerState, [ArrangeGroupProps]>(arrangeGroup);
